import { PersonOrgType } from '@ndvr/customer-api';

import { inputClass, linkButtonClass, NativeSelect, Option, RemoveButton } from './fields';
import {
    entityRolesFor,
    isHundred,
    PERCENT_ROLE_LABELS,
    percentTotals,
    percentTypeOf,
    RelatedPerson,
    splitPercentsEvenly,
} from './onboarding-model';

/** A new row of an entity: no person yet, the type's first role, and the percents split again. */
export const withNewRow = (rows: RelatedPerson[], entityType: PersonOrgType) =>
    splitPercentsEvenly([
        ...rows,
        { personOrgGUID: '', role: entityRolesFor(entityType)[0]?.value ?? '', percent: '' },
    ]);

/**
 * An entity's people, each with a role of the entity's own type and, for roles that carry one, a membership percent.
 * Adding, removing or changing a role splits each role's percents evenly again; a typed percent stays until then.
 * "Add new person…" opens Add person.
 */
export const RelatedPeopleRows = ({
    entityType,
    rows,
    onChange,
    personOptions,
    onAddNew,
}: {
    entityType: PersonOrgType;
    rows: RelatedPerson[];
    onChange: (rows: RelatedPerson[]) => void;
    personOptions: Option[];
    onAddNew?: (index: number) => void;
}) => {
    const roles = entityRolesFor(entityType);
    const incomplete = percentTotals(rows).filter(({ total }) => !isHundred(total));
    return (
        <div className="flex max-w-[760px] flex-col gap-2.5">
            <div className="text-cocoa-600 grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_90px_24px] gap-2.5 text-xs">
                <span>Person</span>
                <span>Relationship to entity</span>
                <span>Percent</span>
                <span />
            </div>
            {rows.map((row, index) => {
                const update = (patch: Partial<RelatedPerson>) =>
                    onChange(rows.map((other, j) => (j === index ? { ...other, ...patch } : other)));
                const takesPercent = !!percentTypeOf(row.role);
                return (
                    <div
                        key={index}
                        className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_90px_24px] items-center gap-2.5"
                    >
                        <NativeSelect
                            aria-label="Person"
                            value={row.personOrgGUID}
                            invalid={!row.personOrgGUID}
                            placeholder="Select person…"
                            options={[
                                ...personOptions,
                                ...(onAddNew ? [{ value: 'new', label: 'Add new person…' }] : []),
                            ]}
                            onChange={(value) =>
                                value === 'new' ? onAddNew?.(index) : update({ personOrgGUID: value })
                            }
                        />
                        <NativeSelect
                            aria-label="Relationship to entity"
                            value={row.role}
                            invalid={!row.role}
                            placeholder={row.role ? undefined : 'Select role…'}
                            options={roles}
                            onChange={(role) =>
                                onChange(
                                    splitPercentsEvenly(
                                        rows.map((other, j) => (j === index ? { ...other, role } : other)),
                                    ),
                                )
                            }
                        />
                        {takesPercent ? (
                            <input
                                aria-label="Percent"
                                inputMode="decimal"
                                className={inputClass(incomplete.some(({ type }) => type === percentTypeOf(row.role)))}
                                value={row.percent}
                                onChange={(event) => update({ percent: event.target.value.replace(/[^\d.]/g, '') })}
                            />
                        ) : (
                            <span className="text-cocoa-400 text-sm">—</span>
                        )}
                        <RemoveButton
                            onClick={() => onChange(splitPercentsEvenly(rows.filter((_, j) => j !== index)))}
                        />
                    </div>
                );
            })}
            <div className="flex items-center justify-between gap-3">
                <button
                    type="button"
                    className={linkButtonClass}
                    onClick={() => onChange(withNewRow(rows, entityType))}
                >
                    Add related people
                </button>
                {incomplete.length > 0 && (
                    <span className="text-gold-600 text-[13px] font-medium">
                        {incomplete
                            .map(({ type, total }) => `${PERCENT_ROLE_LABELS[type] ?? type} ${total}%`)
                            .join(', ')}{' '}
                        — each must total 100%
                    </span>
                )}
            </div>
        </div>
    );
};
